"use client";

import { useEffect, useRef, useState } from "react";

export default function GuideDemoVideo() {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);

  useEffect(() => {
    const containerElement = containerRef.current;
    if (!containerElement) {
      return;
    }

    if (!("IntersectionObserver" in globalThis)) {
      const loadTimer = globalThis.setTimeout(() => {
        setShouldLoadVideo(true);
      }, 0);
      return () => {
        globalThis.clearTimeout(loadTimer);
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const intersectsViewport = entries.some((entry) => entry.isIntersecting);
        if (intersectsViewport) {
          setShouldLoadVideo(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px 0px" },
    );

    observer.observe(containerElement);
    return () => {
      observer.disconnect();
    };
  }, []);

  const handleFullscreen = async () => {
    const videoElement = videoRef.current;
    if (!videoElement || !videoElement.requestFullscreen) {
      return;
    }

    try {
      await videoElement.requestFullscreen();
    } catch (error) {
      console.error("Unable to enter full screen mode.", error);
    }
  };

  return (
    <div className="mx-auto mt-6 flex max-w-4xl justify-center">
      <div className="w-[640px] max-w-full">
        <div ref={containerRef} className="relative">
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="none"
            controls
            onLoadedData={() => {
              setIsVideoReady(true);
            }}
            onError={() => {
              setIsVideoReady(true);
            }}
            className="aspect-video h-auto w-full rounded-xl border border-white/15 bg-black object-contain shadow-glass"
          >
            {shouldLoadVideo ? (
              <>
                <source src="/short-cls-demo-wide.webm" type="video/webm" />
                <source src="/short-cls-demo-wide.mp4" type="video/mp4" />
              </>
            ) : null}
          </video>
          {isVideoReady ? null : (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl bg-black/45">
              <span
                aria-label="Loading video"
                className="h-10 w-10 animate-spin rounded-full border-4 border-white/25 border-t-white"
                role="status"
              />
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            void handleFullscreen();
          }}
          className="mt-3 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
        >
          Full screen
        </button>
      </div>
    </div>
  );
}
